import { useState, useEffect } from 'react';
import { fetchOrganizations, Organization, UserNotActivatedError } from '../services/orgService';

/**
 * Interface for the result object returned by useOrganizations
 */
interface UseOrganizationsResult {
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  isUnactivated: boolean;
  refetch: () => void;
}

/**
 * Custom hook for fetching organizations
 * Automatically fetches organizations when the component mounts
 * 
 * @returns Object containing organizations data, loading state, error state, and refetch function
 */
const useOrganizations = (): UseOrganizationsResult => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isUnactivated, setIsUnactivated] = useState<boolean>(false);

  /**
   * Fetch organizations data
   */
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsUnactivated(false);
      const data = await fetchOrganizations();
      setOrganizations(data);
    } catch (err) {
      console.error('Error fetching organizations:', err);

      // Handle specific error types
      if (err instanceof UserNotActivatedError) {
        setIsUnactivated(true);
        setError('Your account is not associated with any organizations in this region.');
      } else {
        setError('Unable to retrieve organizations. Please try again later.');
      }
      // Don't reset organizations on error - keep previous data
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refetch organizations data
   */
  const refetch = () => {
    fetchData();
  };

  // Fetch organizations on component mount
  useEffect(() => {
    fetchData();
  }, []);

  return {
    organizations,
    loading,
    error,
    isUnactivated,
    refetch
  };
};

export default useOrganizations;
